---
title: What BibaCV is
description: Camera analytics for retail stores, explained from the top.
---

BibaCV turns a store's security cameras into three numbers per day:

1. **Entries and exits.** How many people came in and left, hour by hour.
2. **Aisle dwell.** How long people stayed in each area of the store.
3. **Customer journeys.** The paths people walked across the floor.

Nothing streams to a cloud. Each store has one GPU machine on site. Overnight,
that machine downloads the day's recordings, watches them once, writes a
report, and goes idle. The footage never leaves the store.

## The parts

The system is three services in one repository:

| Service     | What it is                                | Where it runs           |
| ----------- | ----------------------------------------- | ----------------------- |
| Frontend    | The web app you are reading this in       | Anywhere (port 3001)    |
| Gateway API | Accounts, store settings, run control     | Anywhere (port 3000)    |
| ML service  | The vision pipeline and its control plane | The store's GPU machine |

The gateway and the ML service share no database. The gateway drives the GPU
machine over plain HTTP, and the pipeline's stages exchange data as files and
Kafka messages. This keeps the boundary simple: settings go down, reports come
back, video stays put.

## A day in the life

1. During opening hours, the store's cameras record. Nothing else happens.
2. After close, the recorder (or an upload job, or a person using this app)
   puts the night's files in the store's bucket.
3. At the scheduled hour, the GPU machine fetches the night, checks that every
   camera's video lines up, and runs the vision pipeline.
4. The pipeline finds people, follows them across cameras as single
   identities, and converts their movements into counts, dwell times and
   journeys in floor-plan meters.
5. A report is written, with quality flags that say whether the numbers can be
   trusted. Flagged events can optionally get a second opinion from a local
   vision-language model.
6. The GPU goes idle until the next night.

## Where to go next

- [Architecture](/docs/architecture) shows the whole system on one page.
- [The nightly pipeline](/docs/pipeline) walks through each processing stage.
- [Services](/docs/services) describes every running part and its job.
- [Data and storage](/docs/data-and-storage) explains what data lives where,
  and why analytics uses ClickHouse.
- [Configuration](/docs/configuration) explains how settings reach the GPU box.
- [Operations](/docs/operations) covers running nights and debugging them.
